import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ArrowRight, GraduationCap, HeartPulse, Landmark, Scale, Shield } from 'lucide-react';

import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { Badge } from '@/components/common/Badge/Badge';
import { Card } from '@/components/common/Card/Card';
import { AiResponseCard } from '@/components/home/AiResponseCard/AiResponseCard';
import { CandidateSelector } from '@/components/home/CandidateSelector/CandidateSelector';
import { Button } from '@/components/common/Button/Button';
import { QuestionInput } from '@/components/home/QuestionInput/QuestionInput';
import { homeProcessingMessages } from '@/data/statusMessages';
import { buildAnalyticsEventName, trackAnalyticsEvent } from '@/lib/analytics';
import { getAnswerCacheById } from '@/services/answer-cache-service';
import { useCandidates } from '@/hooks/use-candidates';
import { normalizeFirebaseError, useProposalQuestion } from '@/hooks/use-proposal-question';
import { useTopics } from '@/hooks/use-topics';
import { useBrowserQueryCache } from '@/lib/query-cache/use-browser-query-cache';
import { trackGoogleAdsPageViewConversion } from '@/lib/google-ads';
import { useSeo } from '@/hooks/useSeo';
import { buildBrandJsonLd, buildPageTitle } from '@/lib/seo';
import { buildCandidateProposalPath } from '@/utils/candidateProposalRoute';
import { classNames } from '@/utils/classNames';
import { toProposalQuestionViewState } from '@/adapters/answer-adapter';

import layoutStyles from '@/styles/layout.module.css';
import styles from '@/styles/home.module.css';

const questionSuggestions = [
  {
    id: 'education',
    title: 'Educação',
    example: 'O que propõe para a educação?',
    icon: GraduationCap,
  },
  {
    id: 'health',
    title: 'Saúde',
    example: 'O que propõe para a saúde?',
    icon: HeartPulse,
  },
  {
    id: 'security',
    title: 'Segurança pública',
    example: 'O que propõe para reduzir a violência urbana?',
    icon: Shield,
  },
  {
    id: 'economy',
    title: 'Economia',
    example: 'Quais são as propostas econômicas?',
    icon: Landmark,
  },
] as const;

export function HomePage(): JSX.Element {
  const candidatesState = useCandidates();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const queryCache = useBrowserQueryCache();
  const questionInputRef = useRef<HTMLTextAreaElement | null>(null);
  const structuredData = useMemo(() => buildBrandJsonLd('/'), []);
  const answerCacheId = searchParams.get('iar')?.trim() ?? '';
  const topicsState = useTopics(Boolean(answerCacheId));
  const isCandidatesLoading = candidatesState.status === 'loading';
  const [questionText, setQuestionText] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const { state: response, askQuestion, setState: setResponseState } = useProposalQuestion();
  const [sharedSeoContext, setSharedSeoContext] = useState<{ candidateId: string; topicId?: string } | null>(null);

  const heroSection = (
    <section className={styles.heroSection} aria-labelledby="home-hero-title">
      <div className={styles.sectionIntro}>
        <p className={layoutStyles.sectionTitle}>Consulta pública</p>
        <h1 id="home-hero-title" className={styles.heroTitle}>
          Consulte propostas eleitorais e compare candidatos
        </h1>
        <p className={styles.heroDescription}>
          O Verde Confirma reúne propostas oficiais, metodologia explicada e páginas institucionais para que você navegue com clareza, sem ranking e sem recomendação de voto.
        </p>

        <div className={styles.heroPills} aria-label="Princípios do site">
          <Badge tone="neutral">Fontes oficiais</Badge>
          <Badge tone="neutral">Sem ranking</Badge>
          <Badge tone="neutral">Privacidade local</Badge>
        </div>

        <div className={styles.heroActions}>
          <Button to="/match-politico/metodologia" variant="secondary" size="sm">
            Ver metodologia
          </Button>
          <Button to="/privacidade" variant="ghost" size="sm">
            Privacidade
          </Button>
        </div>
      </div>

      <div className={styles.heroHighlightsGrid}>
        <Card tone="soft" className={styles.heroHighlightCard}>
          <p className={styles.heroHighlightTitle}>Fontes oficiais</p>
          <p className={styles.heroHighlightText}>
            Cada resposta aponta para documentos públicos e trechos verificáveis, para você conferir a origem do conteúdo.
          </p>
        </Card>

        <Card tone="soft" className={styles.heroHighlightCard}>
          <p className={styles.heroHighlightTitle}>Sem ranking</p>
          <p className={styles.heroHighlightText}>
            O site descreve posições e limites de interpretação, sem indicar voto ou transformar o resultado em placar.
          </p>
        </Card>

        <Card tone="soft" className={styles.heroHighlightCard}>
          <p className={styles.heroHighlightTitle}>Dados locais</p>
          <p className={styles.heroHighlightText}>
            As respostas do Match Político permanecem no navegador até você limpar os dados do dispositivo.
          </p>
        </Card>
      </div>
    </section>
  );

  const sharedSeo = useMemo(() => {
    if (!answerCacheId || !sharedSeoContext?.candidateId || !sharedSeoContext.topicId) {
      return null;
    }

    const candidate = candidatesState.candidates.find((entry) => entry.id === sharedSeoContext.candidateId) ?? null;
    const topic = topicsState.status === 'success' ? topicsState.topics.find((entry) => entry.id === sharedSeoContext.topicId) ?? null : null;

    if (!candidate || !topic) {
      return null;
    }

    const title = `Veja as propostas de ${candidate.name} para ${topic.label}`;
    const description = `Conheça as propostas apresentadas para ${topic.label} e consulte as fontes oficiais.`;

    return {
      title: buildPageTitle(title),
      description,
      ogTitle: title,
      ogDescription: description,
      image: candidate.photoUrl,
      imageAlt: `${candidate.name} no Verde Confirma`,
      canonicalPath: `/share?iar=${answerCacheId}`,
    };
  }, [answerCacheId, candidatesState.candidates, sharedSeoContext, topicsState.status, topicsState.topics]);

  useSeo({
    title: sharedSeo?.title ?? buildPageTitle('Consulte propostas eleitorais e compare candidatos'),
    description:
      sharedSeo?.description ??
      'Consulte propostas eleitorais, compare candidatos e explore o Match Político para entender posições políticas com rapidez.',
    canonicalPath: answerCacheId ? `/share?iar=${answerCacheId}` : '/',
    image: sharedSeo?.image,
    imageAlt: sharedSeo?.imageAlt,
    ogTitle: sharedSeo?.ogTitle,
    ogDescription: sharedSeo?.ogDescription,
    ogType: sharedSeo ? 'article' : 'website',
    structuredData,
  });

  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    trackGoogleAdsPageViewConversion();
  }, [location.pathname]);

  const selectedCandidate =
    candidatesState.candidates.find((candidate) => candidate.id === selectedCandidateId) ??
    candidatesState.candidates[0] ??
    null;

  useEffect(() => {
    if (candidatesState.status !== 'success' || candidatesState.candidates.length === 0) {
      return;
    }

    if (
      !selectedCandidateId ||
      !candidatesState.candidates.some((candidate) => candidate.id === selectedCandidateId)
    ) {
      setSelectedCandidateId(candidatesState.candidates[0]!.id);
    }
  }, [candidatesState.candidates, candidatesState.status, selectedCandidateId]);

  useEffect(() => {
    if (!answerCacheId) {
      setSharedSeoContext(null);
      return;
    }

    let active = true;

    setResponseState({ status: 'loading' });
    setSharedSeoContext(null);

    void getAnswerCacheById(answerCacheId, { cache: queryCache })
      .then((cachedResponse) => {
        if (!active) {
          return;
        }

        setResponseState(toProposalQuestionViewState(cachedResponse));

        const nextCandidateId = cachedResponse.context.candidateIds[0];
        const nextTopicId = cachedResponse.context.topicIds[0];

        setSharedSeoContext({
          candidateId: nextCandidateId ?? '',
          topicId: nextTopicId,
        });

        if (nextCandidateId) {
          setSelectedCandidateId(nextCandidateId);
        }
      })
      .catch((error) => {
        if (!active) {
          return;
        }

        setResponseState({
          status: 'error',
          message: normalizeFirebaseError(error),
        });
      });

    return () => {
      active = false;
    };
  }, [answerCacheId, queryCache, setResponseState, setSelectedCandidateId]);

  const handleSubmit = (): void => {
    const trimmedQuestion = questionText.trim();

    if (!trimmedQuestion || !selectedCandidate || response.status === 'loading') {
      return;
    }

    void askQuestion(trimmedQuestion, { id: selectedCandidate.id, name: selectedCandidate.name });
  };

  const selectedCandidateName = selectedCandidate?.name ?? candidatesState.candidates[0]?.name ?? 'candidato selecionado';
  const selectedCandidateProposalUrl = selectedCandidate ? buildCandidateProposalPath(selectedCandidate.id) : undefined;

  if (candidatesState.status === 'error') {
    return (
      <section className={styles.section}>
        <EmptyState
          title="Não foi possível carregar os candidatos"
          description={candidatesState.error ?? ''}
          tone="danger"
        />
        {heroSection}
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <CandidateSelector
        candidates={candidatesState.candidates}
        selectedCandidateId={selectedCandidateId}
        onSelectCandidate={setSelectedCandidateId}
        loading={isCandidatesLoading}
      />

      <section className={styles.selectedCandidateSection} aria-labelledby="question-lead-title" aria-busy={isCandidatesLoading}>
        {isCandidatesLoading && (
          <div className={styles.selectedCandidateCard} aria-hidden="true">
            <div className={classNames(styles.skeleton, styles.selectedCandidateAvatarSkeleton)} />

            <div className={styles.selectedCandidateCopy}>
              <div className={classNames(styles.skeleton, styles.selectedCandidateNameSkeleton)} />
              <div className={classNames(styles.skeleton, styles.selectedCandidatePartySkeleton)} />
              <div className={styles.selectedCandidateMeta}>
                <div className={classNames(styles.skeleton, styles.selectedCandidateMetaSkeleton)} />
                <div className={classNames(styles.skeleton, styles.selectedCandidateMetaSkeleton)} />
              </div>
            </div>
          </div>
        )}

        <section className={styles.questionLeadSection} aria-labelledby="question-lead-title">
          <p id="question-lead-title" className={styles.questionLead}>
            Pergunte sobre as propostas de{' '}
            {isCandidatesLoading ? (
              <span className={classNames(styles.skeleton, styles.questionLeadSkeletonName)} aria-hidden="true" />
            ) : (
              <span className={styles.questionLeadHighlight}>{selectedCandidateName}</span>
            )}
          </p>
          {isCandidatesLoading ? (
            <div className={classNames(styles.skeleton, styles.selectedCandidateButtonSkeleton)} aria-hidden="true" />
          ) : selectedCandidateProposalUrl ? (
            <Button
              to={selectedCandidateProposalUrl}
              variant="secondary"
              size="sm"
              fullWidth
              className={styles.selectedCandidateButton}
              ariaLabel={`Ver proposta completa de ${selectedCandidateName}`}
            >
              Ver proposta completa
              <ArrowRight size={16} />
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              fullWidth
              disabled
              className={styles.selectedCandidateButton}
            >
              Ver proposta completa
              <ArrowRight size={16} />
            </Button>
          )}
        </section>
      </section>

      <QuestionInput
        value={questionText}
        onChange={setQuestionText}
        onSubmit={handleSubmit}
        isLoading={response.status === 'loading'}
        disabled={!selectedCandidate}
        inputRef={questionInputRef}
        skeleton={isCandidatesLoading}
      />

      {response.status !== 'idle' ? (
        <section className={styles.responseSection} aria-live="polite">
          <AiResponseCard
            response={response}
            loadingMessages={homeProcessingMessages}
            sharePath="/share"
          />
        </section>
      ) : null}

      <section className={styles.suggestionsSection} aria-labelledby="home-suggestions-title">
        <h2 id="home-suggestions-title" className={layoutStyles.sectionTitle}>
          Sugestões de perguntas
        </h2>

        <div className={styles.suggestionsGrid}>
          {questionSuggestions.map(({ id, title, example, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={styles.suggestionCard}
              onClick={() => {
                trackAnalyticsEvent(buildAnalyticsEventName(`Sugestão ${title}`, `home:suggestion:${id}`));
                setQuestionText(example);
                questionInputRef.current?.focus();
              }}
              aria-label={`Usar sugestão de ${title}: ${example}`}
            >
              <span className={styles.suggestionIcon} aria-hidden="true">
                <Icon size={18} />
              </span>

              <span className={styles.suggestionCopy}>
                <span className={styles.suggestionTitle}>{title}</span>
                <span className={styles.suggestionExample}>{example}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <aside className={styles.responseCtaRow} aria-label="Atalho para comparações">
        <span className={styles.comparisonCtaIcon} aria-hidden="true">
          <Scale size={28} />
        </span>

        <div className={styles.responseCtaCopy}>
          <p className={styles.responseCtaTitle}>Match político</p>
          <p className={styles.responseCtaText}>
            Responda algumas questões e veja como os candidatos se posicionam sobre os temas que importam para você.
          </p>
        </div>

        <Button to="/match-politico" variant="secondary" size="lg" className={styles.responseCtaButton}>
          Quero comparar minhas posições
          <ArrowRight size={16} />
        </Button>
      </aside>

      {heroSection}
    </section>
  );
}
